import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@material-ui/core";
import { AppBarButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { SHARE_PORTAL_ROOT } from "../../../../config";
import { useLocation } from "react-router-dom";
import { Autocomplete } from "@material-ui/lab";
import { Error } from "../../common/Error";
import { useDispatch, useSelector } from "react-redux";
import { orgSelector } from "../../../redux/selectors";
import { ShareRecord } from "../../../domain/ShareRecord";
import { updatingShare } from "../../../redux/actions";

const EMAIL_REGEX = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

export const ShareButtonAndDialog: React.FC<{ id: string }> = ({ id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { share } = useSelector(orgSelector);

  return (
    <>
      <AppBarButton
        text="Share"
        data-test="share-button"
        tooltip="Share"
        onClick={() => setIsOpen(true)}
      />
      {isOpen && share && (
          <ShareDialog share={share} id={id} onClose={() => setIsOpen(false)} />
        )}
    </>
  );
};

type ShareDialogProps = {
  id: string;
  share: ShareRecord;
  onClose: () => void;
};

const ShareDialog: React.FC<ShareDialogProps> = ({ id, share, onClose }) => {
  const dispatch = useDispatch();


  const { control, errors, handleSubmit, formState } = useForm({
    defaultValues: share,
    reValidateMode: "onBlur",
  });

  const { pathname } = useLocation();

  const onSubmit = (share: ShareRecord) => {
    dispatch(updatingShare(id, share));
  };

  const hasShareUpdated = useHasReduxObjectUpdated(share);

  useEffect(() => {
    if (formState.isSubmitted && hasShareUpdated) {
      onClose();
    }
  }, [hasShareUpdated, formState.isSubmitted, onClose, id]);

  return (
    <>
      <Dialog open={true} onClose={onClose} maxWidth="md">
        <DialogTitle>Share Settings</DialogTitle>

        <DialogContent>
          <form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                You can share this Organisation with people who don't have permission or who don't have 
                a portal account. The link below is accessible to people who
                have access to the email addresses below.
              </Grid>
              <Grid item xs={12}>
                <TextField
                  readOnly
                  value={`${SHARE_PORTAL_ROOT}${pathname}`}
                  label="Share link"
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="whitelist"
                  rules={{
                    validate: {
                      regex: (value: string[]) =>
                        value.every((i: string) => EMAIL_REGEX.test(i)),
                    },
                  }}
                  control={control}
                  render={({ onChange, ...props }) => (
                    <Autocomplete
                      multiple
                      autoSelect
                      freeSolo
                      options={[]}
                      ChipProps={{ color: "primary" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="whitelist"
                          variant="outlined"
                          label="Email Addresses"
                        />
                      )}
                      onChange={(_: unknown, data: string[]) => onChange(data)}
                      {...props}
                      data-test="share-allowlist-autocomplete"
                    />
                  )}
                />
                {errors.whitelist && (
                  <Error>Please enter only valid email addresses</Error>
                )}
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
          <Button data-test="share-confirm-button" onClick={handleSubmit(onSubmit)} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useHasReduxObjectUpdated = (object: any) => {
  const ref = useRef(object);
  return object !== ref.current;
};
